import { ApiError } from '@ctcells/api'
import type { Sample } from '@ctcells/contract'
import { errorMessage, sampleStatusLabel, t } from '@ctcells/i18n'

import { DISPLAY_TIME_ZONE } from '../format.ts'

import type { RegisterDraft, RegisterOutcome } from './SampleNewScreen.tsx'

/**
 * 화면의 문자열 초안 → 계약 본문. 빈 칸은 **키 자체를 빼고** 보낸다
 * (`exactOptionalPropertyTypes` 라 `undefined` 를 값으로 넣을 수 없기도 하다).
 * `metadata` 는 초안에 있을 때만 그대로 싣는다 — 시료 추가가 짓는다(`sampleRows.ts` `entryDraft`).
 * ⚠ 적혈구 농도(`rbc_concentration` 열)는 **새 작업 화면의 빠른 등록만** 아직 보낸다 — 시료 추가는
 *   2026-10-04 에 그 칸을 걷고 추가 입력(metadata)으로 옮겼다(eric).
 */
export function registerSampleVars(draft: RegisterDraft) {
  const text = (value: string | undefined) => {
    const trimmed = value?.trim() ?? ''
    return trimmed === '' ? undefined : trimmed
  }
  // ⚠ 적혈구 농도만 숫자다. 숫자가 아니면 보내지 않는다 — 0 으로 바꾸면 없던 값이 생긴다
  const rbcRaw = text(draft.rbc_concentration)
  const rbc = rbcRaw === undefined ? Number.NaN : Number(rbcRaw)

  const patientId = text(draft.patient_id)
  const sampleType = text(draft.sample_type)
  const bloodDrawDate = text(draft.blood_draw_date)
  const cancerType = text(draft.cancer_type)
  const cancerStage = text(draft.cancer_stage)
  const metadata =
    draft.metadata === undefined || Object.keys(draft.metadata).length === 0
      ? undefined
      : draft.metadata

  return {
    sampleId: draft.sample_id.trim(),
    ...(patientId === undefined ? {} : { patientId }),
    ...(sampleType === undefined ? {} : { sampleType }),
    ...(Number.isNaN(rbc) ? {} : { rbcConcentration: rbc }),
    ...(bloodDrawDate === undefined ? {} : { bloodDrawDate }),
    ...(cancerType === undefined ? {} : { cancerType }),
    ...(cancerStage === undefined ? {} : { cancerStage }),
    ...(metadata === undefined ? {} : { metadata }),
  }
}

const ISO_DAY = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: DISPLAY_TIME_ZONE,
})

/** 표시 시간대 기준 오늘 `YYYY-MM-DD` — 채혈 날짜의 「오늘」 · 미래 날짜 경고가 같은 날을 본다 */
export function todayIso(now: Date = new Date()): string {
  return ISO_DAY.format(now)
}

/** 채혈 날짜가 오늘보다 뒤인가 — ISO 날짜라 글자 비교가 곧 날짜 비교다. 경고만 한다(서버는 막지 않는다) */
export function isFutureDay(dayIso: string, todayIsoValue: string): boolean {
  return dayIso.trim() !== '' && dayIso > todayIsoValue
}

/**
 * 등록 실패 → 줄 결과. 🔴 **서버가 준 코드 · 문구를 그대로 옮긴다.** 409 `SAMPLE_ALREADY_EXISTS` 만
 * 번호 중복이다. 지금 실물은 중복을 500 `DB_WRITE_FAIL` 로 낸다(요청 102) — **그것을 중복으로
 * 추론하지 않는다.** 서버가 고쳐지면 그 줄 번호 칸에 「이미 등록된 시료 번호」가 선다.
 */
export function registerFailureOf(error: unknown): Extract<RegisterOutcome, { ok: false }> {
  const api = error instanceof ApiError ? error : null
  return {
    ok: false,
    duplicate: api?.error_code === 'SAMPLE_ALREADY_EXISTS',
    message: errorMessage(api),
  }
}

/** 등록 직후도 in_process이므로 서버의 작업 배정 여부를 함께 표시한다. */
export function sampleStateLabel(sample: Sample): string {
  return sample.status === 'in_process' && sample.batch_id == null
    ? t('sample.status.available')
    : sampleStatusLabel(sample.status)
}
